import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { FOCUS_RING, PORTAL_SURFACE } from "./classes";

const WIDTHS = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-2xl" };

/**
 * A centred modal on the raised surface (wireframes: "Send broadcast?", "Erase Nadia's
 * data?"). The content is portalled, so it carries its own font and colours
 * (PORTAL_SURFACE) instead of inheriting the section's.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.footer] right-aligned action row
 * @param {"sm"|"md"|"lg"} [props.size]
 * @param {string} [props.testId]
 */
export default function CrmDialog({ open, onOpenChange, title, description, footer, size = "md", testId, children }) {
  const t = useT("crm");
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content
          data-testid={testId}
          data-lenis-prevent
          className={cn(
            PORTAL_SURFACE,
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6",
            "max-h-[calc(100vh-2rem)] overflow-y-auto",
            WIDTHS[size],
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <DialogPrimitive.Title className="font-display text-2xl leading-tight">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close className={cn("rounded-full p-1 text-eh-muted hover:text-eh-ink", FOCUS_RING)} aria-label={t("common.close")}>
              <X size={18} aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          {description
            ? <DialogPrimitive.Description className="mt-2 text-sm text-eh-muted">{description}</DialogPrimitive.Description>
            : <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>}
          <div className="mt-4">{children}</div>
          {footer && <div className="mt-6 flex flex-wrap items-center justify-end gap-3">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
